import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/** Centred card used by every signed-out screen. */
export function AuthShell({
  title,
  subtitle,
  children,
  footer,
  audience = 'staff',
}: {
  title: string;
  subtitle?: string;
  /** Optional: confirmation screens are just a heading and a line of copy. */
  children?: ReactNode;
  footer?: ReactNode;
  /**
   * Who is reading. Not every screen in this shell belongs to the business:
   * the unsubscribe page is opened by their customer, from an email, and that
   * person is owed the privacy policy and nothing else. Offering them "How
   * this works" and a "Sign in" button describes an account they do not have
   * and a workspace that is not theirs.
   */
  audience?: 'staff' | 'customer';
}): React.JSX.Element {
  return (
    <div className="flex min-h-full items-center justify-center bg-slate-100 px-4 py-12 dark:bg-slate-950">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center">
          <img src="/logo-192.png" alt="LeadGab" width={56} height={56} className="h-14 w-14" />
          <h1 className="mt-4 text-xl font-semibold text-slate-900 dark:text-slate-50">{title}</h1>
          {subtitle ? (
            <p className="mt-1.5 text-center text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
          ) : null}
        </div>

        {children ? <div className="lg-card p-6">{children}</div> : null}

        {footer ? (
          <div className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">{footer}</div>
        ) : null}

        {/*
          Here rather than in each page's own footer: every staff screen owes
          the reader the same links, and an invited agent landing on the
          invitation page is exactly who needs "How this works" most.

          Mixed link types on one line, on purpose. /help is a route in this
          app, so it is a Link — an anchor would reload the bundle and throw
          away whatever was typed in the form above. Privacy and Terms are HTML
          the API renders, so a reviewer's crawler can read a privacy policy
          without running a bundle; those stay plain anchors.
        */}
        <div className="mt-4 text-center text-xs text-slate-400 dark:text-slate-500">
          {audience === 'staff' ? (
            <>
              {/* No colour of its own — the row is one line of three, and the
                  container's grey is the colour for all of them. */}
              <Link to="/help" className="hover:underline">
                How this works
              </Link>
              {' · '}
            </>
          ) : null}

          <a href="/privacy" className="hover:underline">
            Privacy
          </a>
          {' · '}
          <a href="/terms" className="hover:underline">
            Terms
          </a>
        </div>
      </div>
    </div>
  );
}
